import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  booleanAttribute,
} from '@angular/core';

/** Length (% of its column) of each placeholder cell of a row, repeated for as many columns as asked. */
const CELL_WIDTHS = [90, 60, 75, 50, 70, 45, 80, 55];
/** Longest text bar under a table header (px): placeholder text, not a full-width stripe. */
const MAX_TEXT_BAR = 180;

/** A placeholder bar under one header cell of the table it lines up with. */
interface AlignedCell {
  kind: 'text' | 'box';
  left: number;
  width: number;
}

/**
 * Grey placeholders with a moving shine, shown where data will appear while it loads for the first time, so the
 * page keeps its shape (UHD-1736). Rows for tables and lists; blocks to draw the parts of a card in its real size.
 */
@Component({
  selector: 'app-skeleton',
  templateUrl: './skeleton.component.html',
  styleUrls: ['./skeleton.component.scss'],
  standalone: false,
  host: { role: 'status', 'aria-busy': 'true', 'aria-label': 'Loading', 'data-testId': 'skeleton' },
})
export class SkeletonComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() type: 'rows' | 'block' = 'rows';
  /** Rows to show (rows). */
  @Input() count = 6;
  /** Cells per row (rows). */
  @Input() columns = 5;
  /** Use the height of the table's real rows. */
  @Input() rowHeight = '52px';
  /**
   * Rows line up with the header of the table they sit in, or the nearest table next to them: one bar under each
   * header, on the side its text sits (left, right or centred); a square under a checkbox header; nothing under an
   * empty one (row menus). The columns follow the table when it changes (rows).
   */
  @Input({ transform: booleanAttribute }) alignToTable = false;
  /** Size and corners of the block (block). */
  @Input() height = '120px';
  @Input() width = '100%';
  @Input() radius = '6px';

  /**
   * An asked width goes on the element itself, so a % width also works inside a flex row. Without one the element
   * keeps the width its page gives it.
   */
  @HostBinding('style.width') get hostWidth(): string | null {
    return this.type === 'block' && this.width !== '100%' ? this.width : null;
  }

  items: number[] = [];
  cells: number[] = [];
  /** alignToTable: the bars of each row, and where the rows start and how wide they are (the table's). */
  alignedRows: AlignedCell[][] = [];
  alignedLeft = 0;

  private table: HTMLElement | null = null;
  private resizeObserver?: ResizeObserver;
  private frame = 0;

  constructor(private host: ElementRef<HTMLElement>, private changeDetector: ChangeDetectorRef) {}

  ngOnChanges(): void {
    this.items = Array.from({ length: this.count }, (_, i) => i);
    this.cells = Array.from({ length: this.columns }, (_, i) => CELL_WIDTHS[i % CELL_WIDTHS.length]);
  }

  ngAfterViewInit(): void {
    if (!this.alignToTable || this.type !== 'rows' || typeof ResizeObserver === 'undefined') {
      return;
    }
    this.table = this.findTable();
    if (!this.table) {
      return;
    }
    // Measures once laid out, and again whenever the table or the placeholder changes size (columns shown, window).
    this.resizeObserver = new ResizeObserver(() => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.alignToHeader());
    });
    this.resizeObserver.observe(this.table);
    this.resizeObserver.observe(this.host.nativeElement);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.frame);
  }

  /** The table the placeholder is in, or the nearest one around it. */
  private findTable(): HTMLElement | null {
    const host = this.host.nativeElement;
    const own = host.closest('table');
    if (own) {
      return own;
    }
    for (let el = host.parentElement; el; el = el.parentElement) {
      const table = el.querySelector('table');
      if (table) {
        return table;
      }
    }
    return null;
  }

  /** Where a header's letters are drawn (left and right edge), or null without text. */
  private textSpan(th: HTMLElement): { left: number; right: number } | null {
    const walker = document.createTreeWalker(th, NodeFilter.SHOW_TEXT);
    const range = document.createRange();
    let left = Infinity;
    let right = -Infinity;
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      if (!node.textContent?.trim()) {
        continue;
      }
      range.selectNodeContents(node);
      const box = range.getBoundingClientRect();
      left = Math.min(left, box.left);
      right = Math.max(right, box.right);
    }
    return left === Infinity ? null : { left, right };
  }

  private alignToHeader(): void {
    const table = this.table;
    const header = table?.querySelector('thead tr') ?? table?.querySelector('tr');
    const headerCells = header ? Array.from(header.querySelectorAll<HTMLElement>(':scope > th')) : [];
    if (!table || headerCells.length === 0) {
      return;
    }
    const tableBox = table.getBoundingClientRect();
    const hostBox = this.host.nativeElement.getBoundingClientRect();
    const columns = headerCells
      .map((th) => {
        const box = th.getBoundingClientRect();
        const style = getComputedStyle(th);
        const paddingLeft = parseFloat(style.paddingLeft) || 0;
        const paddingRight = parseFloat(style.paddingRight) || 0;
        const kind: AlignedCell['kind'] | null = th.querySelector('mat-checkbox, input[type="checkbox"]')
          ? 'box'
          : th.textContent?.trim()
            ? 'text'
            : null;
        const contentLeft = box.left + paddingLeft;
        const contentRight = box.right - paddingRight;
        // The bar sits on the side the header's text sits (left, right or centred), lined up with the letters.
        const text = kind === 'text' ? this.textSpan(th) : null;
        const leftGap = text ? text.left - contentLeft : 0;
        const rightGap = text ? contentRight - text.right : 0;
        const align = !text ? 'start' : leftGap > 4 && Math.abs(leftGap - rightGap) < 4 ? 'center' : rightGap + 4 < leftGap ? 'end' : 'start';
        return {
          kind,
          align,
          left: contentLeft - tableBox.left,
          width: contentRight - contentLeft,
          textLeft: (text?.left ?? contentLeft) - tableBox.left,
          textRight: (text?.right ?? contentRight) - tableBox.left,
        };
      })
      .filter((column) => column.kind && column.width > 0);

    // Only the offset: the rows keep the width of their own cell. A width set from the table would make the cell,
    // and so the table, a little wider on every measurement while the placeholder shows.
    this.alignedLeft = tableBox.left - hostBox.left;
    this.alignedRows = this.items.map((row) =>
      columns.map((column, i) => {
        const width =
          column.kind === 'box'
            ? 18
            : Math.max(24, Math.min(column.width, MAX_TEXT_BAR) * (CELL_WIDTHS[(i + row) % CELL_WIDTHS.length] / 100));
        const left =
          column.align === 'end'
            ? column.textRight - width
            : column.align === 'center'
              ? (column.textLeft + column.textRight - width) / 2
              : column.left;
        return { kind: column.kind as AlignedCell['kind'], left: Math.max(column.left, left), width };
      })
    );
    this.changeDetector.detectChanges();
  }
}
